import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/features/auth/AuthProvider";
import { useToast } from "@/shared/hooks/useToast";
import { getErrorMessage, isErroCode } from "@/shared/lib/api/errors";
import { Button } from "@/shared/ui/Button/Button";
import { Modal } from "@/shared/ui/Modal/Modal";
import styles from "./ConsentimentoIa.module.css";
import { ContextoConsentimentoIa } from "./consentimentoIaContexto";
import {
  consentimentoIaService,
  type ConsentimentoIa,
} from "./consentimentoIaService";

type Pedido = {
  estado: ConsentimentoIa;
  responder: (concorda: boolean) => void;
};

/** Permissao de envio de dados a IA (consentimento-ia.md). Fica dentro do AuthProvider. */
export function ConsentimentoIaProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const idUsuario = user?.idUsuario ?? null;
  const toast = useToast();
  // Quem ja aceitou nesta sessao nao volta a perguntar a API a cada fala.
  const aceitoPor = useRef<number | null>(null);
  const [pedido, setPedido] = useState<Pedido | null>(null);

  useEffect(() => {
    if (aceitoPor.current !== idUsuario) aceitoPor.current = null;
  }, [idUsuario]);

  const perguntar = useCallback(
    (estado: ConsentimentoIa) =>
      new Promise<boolean>((resolve) => {
        setPedido({
          estado,
          responder: (concorda) => {
            setPedido(null);
            resolve(concorda);
          },
        });
      }),
    [],
  );

  const garantir = useCallback(async () => {
    if (idUsuario != null && aceitoPor.current === idUsuario) return true;
    try {
      let estado = await consentimentoIaService.obter();
      while (!estado.aceito) {
        if (!(await perguntar(estado))) return false;
        try {
          estado = await consentimentoIaService.aceitar(estado.versao);
        } catch (err) {
          // O texto mudou entre a leitura e o aceite: mostra o novo.
          if (!isErroCode(err, "CONSENTIMENTO_IA_VERSAO_DESATUALIZADA"))
            throw err;
          estado = await consentimentoIaService.obter();
        }
      }
      aceitoPor.current = idUsuario;
      return true;
    } catch (err) {
      toast.error(
        getErrorMessage(err, "Não foi possível confirmar a permissão."),
      );
      return false;
    }
  }, [idUsuario, perguntar, toast]);

  const esquecer = useCallback(() => {
    aceitoPor.current = null;
  }, []);

  const valor = useMemo(() => ({ garantir, esquecer }), [garantir, esquecer]);

  return (
    <ContextoConsentimentoIa.Provider value={valor}>
      {children}
      <TextoConsentimentoIa
        estado={pedido?.estado ?? null}
        onResponder={(concorda) => pedido?.responder(concorda)}
      />
    </ContextoConsentimentoIa.Provider>
  );
}

export function TextoConsentimentoIa({
  estado,
  onResponder,
  onFechar,
  rotuloAceitar = "Concordo e continuar",
  rotuloRecusar = "Agora não",
}: {
  estado: ConsentimentoIa | null;
  onResponder: (concorda: boolean) => void;
  /** Fechar pelo X ou Esc. Sem ele, fechar conta como recusar. */
  onFechar?: () => void;
  rotuloAceitar?: string;
  rotuloRecusar?: string;
}) {
  return (
    <Modal
      open={estado != null}
      title={estado?.titulo ?? ""}
      onClose={onFechar ?? (() => onResponder(false))}
      footer={
        <div className={styles.acoes}>
          <Button variant="ghost" onClick={() => onResponder(false)}>
            {rotuloRecusar}
          </Button>
          <Button onClick={() => onResponder(true)}>{rotuloAceitar}</Button>
        </div>
      }
    >
      <div className={styles.texto}>
        {estado?.paragrafos.map((p) => (
          <p key={p}>{p}</p>
        ))}
      </div>
    </Modal>
  );
}
